/**
 * Landing: the platform apex landing page for `rileconnect.org/` (platform mode,
 * no resolved ecosystem tenant). Markets the RILE Connect PLATFORM, not the
 * first tenant on it.
 *
 * This file must NOT render any COSTEM-specific framing: COSTEM has its own
 * landing experience under `/costem/...` (or its custom apex). Content is drawn
 * from the COSTEM working whitepaper (Johnson, Kumin, Rajan, CC BY-SA 4.0); the
 * shared CitationFooter carries the required attribution.
 *
 * Sibling apex pages: `Mission` (`/mission`) and `About` (`/about`). Shared
 * building blocks live in `CitationFooter` and `platformContent`.
 *
 * Routed at `/` again as of #353: ticket #205's pre-launch waitlist page
 * (`ComingSoon`) previously stood in here so the team could develop this
 * component without shipping it, but has since moved to its own
 * `/coming-soon` route — see App.js's `/` and `/coming-soon` Routes for the
 * current wiring. `dev` continues to serve this page at `/` for ongoing
 * development; production serves only `/coming-soon` until launch.
 */
import React from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import SEO from '@/components/common/SEO';
import Hero from '@/components/PlatformHero/PlatformHero';

import CitationFooter from './CitationFooter';
import { WHITEPAPER_WIKI_URL, DESIGN_PILLARS } from './platformContent';
import './Landing.css';

// "What is RILE?": the acronym unpacked one word at a time. Landing-only, so it
// stays co-located here rather than in the shared platformContent module. Light
// paraphrase of the whitepaper's framing; the citation footer carries the
// attribution.
const RILE_WORDS = [
    {
        word: 'Reciprocal',
        body:
            'Knowledge flows in both directions, not just top-down from higher ' +
            'education to schools. The educators driving effective practices ' +
            'on the ground are source authorities, not recipients.',
    },
    {
        word: 'Integrated',
        body:
            'Interdisciplinary, problem-based learning that crosses formal, ' +
            'non-formal, industry, and community lines. The labels matter ' +
            'less than the practice.',
    },
    {
        word: 'Learning Ecosystem',
        body:
            'A metaphor borrowed from ecology: a diverse community of ' +
            'participants in balance, where the health of the whole depends ' +
            'on the diversity of the parts.',
    },
];

const META_DESCRIPTION =
    'RILE Connect is an open platform for hosting learning ecosystems, making ' +
    'learning pathways visible, navigable, and equitable from classroom to ' +
    'career.';

export default function Landing() {
    return (
        <>
            <SEO
                title="RILE Connect"
                description={META_DESCRIPTION}
                isHomePage
            />

            <Hero
                pageTitle="RILE Connect"
                eyebrow="An open platform for learning ecosystems"
                headline="Make learning pathways visible, navigable, and equitable."
                lead={
                    'RILE Connect is open, shared infrastructure for learning ' +
                    'ecosystems: the networks of educators, employers, and ' +
                    'community partners that open pathways from classroom to ' +
                    'career. Effective work is already happening in isolated ' +
                    'places, and RILE Connect connects it, makes it navigable, ' +
                    'and lets any ecosystem run on one open platform.'
                }
                primaryCta={{
                    to: '/ecosystems',
                    label: 'Explore ecosystems',
                }}
                secondaryCta={{
                    to: '/about',
                    label: 'Host your ecosystem',
                }}
            />

            {/* What is RILE?: the acronym, one word per card. */}
            <section className="py-5">
                <Container>
                    <Row className="justify-content-center mb-4">
                        <Col xs={12} md={10} lg={8} className="text-center">
                            <h2 className="h2 text-primary mb-3">
                                What is RILE?
                            </h2>
                            <p className="platform-landing-muted">
                                RILE stands for Reciprocal Integrated Learning
                                Ecosystem. The idea comes from a working
                                whitepaper that grounds this project: learning
                                happens across a web of schools, colleges,
                                employers, and community organizations, and that
                                web works best when it is reciprocal,
                                integrated, and open. Read the full{' '}
                                <a
                                    href={WHITEPAPER_WIKI_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    whitepaper
                                </a>
                                .
                            </p>
                        </Col>
                    </Row>
                    <Row className="g-4 justify-content-center">
                        {RILE_WORDS.map((entry) => (
                            <Col xs={12} md={6} lg={4} key={entry.word}>
                                <div className="h-100 p-4 border rounded-3 platform-landing-rile-card">
                                    <h3 className="h4 text-primary mb-3">
                                        {entry.word}
                                    </h3>
                                    <p className="mb-0">{entry.body}</p>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* The platform model. */}
            <section className="py-5 bg-light">
                <Container>
                    <Row className="justify-content-center mb-4">
                        <Col xs={12} md={10} lg={8} className="text-center">
                            <h2 className="h2 text-primary mb-3">
                                An ecosystem of ecosystems
                            </h2>
                            <p className="platform-landing-muted">
                                The whitepaper called for &ldquo;systems that
                                can be repurposed by other Ecosystems&rdquo; so
                                that &ldquo;other ecosystems can contribute to,
                                adapt and replicate the model.&rdquo; That is
                                what RILE Connect is. Each ecosystem owns its
                                own members, data, and branding, and connects to
                                the others through a shared meta-graph so
                                participants and partners across ecosystems are
                                discoverable to one another.
                            </p>
                        </Col>
                    </Row>
                    <Row className="g-4 justify-content-center">
                        <Col xs={12} md={6} lg={5}>
                            <div className="h-100 p-4 border rounded-3 d-flex flex-column text-center bg-white">
                                <div
                                    className="display-5 mb-2"
                                    aria-hidden="true"
                                >
                                    🗂️
                                </div>
                                <h3 className="h4 text-primary mb-2">
                                    Ecosystems
                                </h3>
                                <p className="platform-landing-muted flex-grow-1">
                                    Browse every ecosystem hosted on the
                                    platform, with member counts and a direct
                                    link into each one.
                                </p>
                                <div className="d-grid">
                                    <Button
                                        as={Link}
                                        to="/ecosystems"
                                        variant="primary"
                                        className="rounded-pill"
                                    >
                                        View the directory
                                    </Button>
                                </div>
                            </div>
                        </Col>
                        <Col xs={12} md={6} lg={5}>
                            <div className="h-100 p-4 border rounded-3 d-flex flex-column text-center bg-white">
                                <div
                                    className="display-5 mb-2"
                                    aria-hidden="true"
                                >
                                    🕸️
                                </div>
                                <h3 className="h4 text-primary mb-2">
                                    The Meta-Graph
                                </h3>
                                <p className="platform-landing-muted flex-grow-1">
                                    Explore the hosted ecosystems as an
                                    interactive graph: each ecosystem is a node
                                    you can select to dive in.
                                </p>
                                <div className="d-grid">
                                    <Button
                                        as={Link}
                                        to="/ecosystems?view=graph"
                                        variant="outline-primary"
                                        className="rounded-pill"
                                    >
                                        Open the graph
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Design pillars: short form. */}
            <section className="py-5">
                <Container>
                    <Row className="justify-content-center mb-4">
                        <Col xs={12} md={10} lg={8} className="text-center">
                            <h2 className="h2 text-primary mb-3">
                                Design pillars
                            </h2>
                            <p className="platform-landing-muted">
                                Five principles that constrain every product
                                decision on the platform.
                            </p>
                        </Col>
                    </Row>
                    <Row className="g-3 justify-content-center">
                        {DESIGN_PILLARS.map((pillar) => (
                            <Col xs={12} sm={6} lg={4} key={pillar.name}>
                                <div className="h-100 p-3 border-start border-3 border-warning bg-light rounded-end">
                                    <h3 className="h5 text-primary mb-2">
                                        {pillar.name}
                                    </h3>
                                    <p className="mb-0 small platform-landing-muted">
                                        {pillar.short}
                                    </p>
                                </div>
                            </Col>
                        ))}
                    </Row>
                    <Row className="justify-content-center mt-4">
                        <Col xs={12} md={10} lg={8} className="text-center">
                            <Button
                                as={Link}
                                to="/mission"
                                variant="outline-primary"
                                className="rounded-pill px-4"
                            >
                                Read the full mission
                            </Button>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Project foundation. */}
            <section className="py-5 bg-light">
                <Container>
                    <Row className="justify-content-center">
                        <Col xs={12} md={10} lg={8}>
                            <h2 className="h2 text-primary mb-3 text-center">
                                Project foundation
                            </h2>
                            <p>
                                RILE Connect is built and operated by the{' '}
                                <a
                                    href="https://c3-lab.org/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    C3 Lab
                                </a>{' '}
                                (Community-Centered Computing) at{' '}
                                <a
                                    href="https://www.msudenver.edu/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    MSU Denver
                                </a>{' '}
                                under a 24-month grant from the{' '}
                                <a
                                    href="https://sipa.colorado.gov/govgrants"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Colorado Statewide Internet Portal Authority
                                    (SIPA) GovGrants Program
                                </a>
                                , January 2026 through December 2027.
                            </p>
                            <p className="mb-0">
                                The first ecosystem hosted on the platform is
                                the Colorado STEM Ecosystem; the architecture is
                                designed to host any number of regional or
                                topical learning ecosystems alongside it. Read
                                more on the <Link to="/about">about page</Link>.
                            </p>
                        </Col>
                    </Row>
                </Container>
            </section>

            <CitationFooter />
        </>
    );
}
